import { useId } from 'react';
import styled from 'styled-components';
import { font } from '../styles/typography';
import { useThemePreference } from '../theme/ThemeContext';

const Selector = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
`;

const Label = styled.span`
  color: var(--spark-color-text);
  font-weight: 500;
  ${font('base')}
`;

const ModeGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3px;
  width: 100%;
  max-width: 330px;
  padding: 4px;
  border: 1px solid var(--spark-color-border);
  border-radius: 12px;
  background: var(--spark-color-surface-muted);

  @media (max-width: 640px) {
    max-width: none;
  }
`;

const ModeButton = styled.button`
  display: flex;
  min-width: 0;
  min-height: 64px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 4px;
  border: 1px solid transparent;
  border-radius: 9px;
  color: var(--spark-color-text-secondary);
  background: transparent;
  cursor: pointer;
  ${font('sm')}

  &[aria-pressed='true'] {
    border-color: color-mix(in srgb, var(--spark-color-brand) 32%, transparent);
    color: var(--spark-color-text);
    background: var(--spark-color-surface);
    box-shadow: 0px 0px 6px 4px var(--spark-color-shadow);
  }

  &:hover:not([aria-pressed='true']) {
    color: var(--spark-color-text);
    background: var(--spark-color-action-hover-surface);
  }

  &:focus-visible {
    z-index: 1;
    outline: 2px solid var(--spark-color-focus);
    outline-offset: 2px;
  }
`;

const IconBubble = styled.span`
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  place-items: center;
  border-radius: 50%;
  color: var(--spark-color-text-muted);
  background: transparent;

  ${ModeButton}[aria-pressed='true'] & {
    color: var(--spark-color-brand);
    background: var(--spark-color-action-hover-surface);
  }
`;

function SystemIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="19" height="19" fill="none">
      <rect x="3.5" y="4" width="17" height="12.5" rx="2.5" fill="currentColor" opacity=".14" />
      <rect x="3.5" y="4" width="17" height="12.5" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8.5 20h7M12 16.5V20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function LightIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="19" height="19" fill="none">
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity=".24" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 2.5v2m0 15v2m9.5-9.5h-2m-15 0h-2m16.22-6.72-1.42 1.42M6.7 17.3l-1.42 1.42m13.44 0-1.42-1.42M6.7 6.7 5.28 5.28" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function DarkIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="19" height="19" fill="none">
      <path d="M19.5 14.1A8 8 0 0 1 9.9 4.5a8.1 8.1 0 1 0 9.6 9.6Z" fill="currentColor" opacity=".24" />
      <path d="M19.5 14.1A8 8 0 0 1 9.9 4.5a8.1 8.1 0 1 0 9.6 9.6Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

const modes = [
  { value: 'system', label: 'Системна', Icon: SystemIcon },
  { value: 'light', label: 'Світла', Icon: LightIcon },
  { value: 'dark', label: 'Темна', Icon: DarkIcon },
];

function ThemeSelector({ showLabel = true }) {
  const id = useId();
  const { mode, setMode } = useThemePreference();

  return (
    <Selector>
      {showLabel && <Label id={id}>Тема</Label>}
      <ModeGroup
        role="group"
        aria-label={showLabel ? undefined : 'Тема'}
        aria-labelledby={showLabel ? id : undefined}
      >
        {modes.map(({ value, label, Icon }) => (
          <ModeButton
            key={value}
            type="button"
            aria-pressed={mode === value}
            onClick={() => setMode(value)}
          >
            <IconBubble><Icon /></IconBubble>
            {label}
          </ModeButton>
        ))}
      </ModeGroup>
    </Selector>
  );
}

export default ThemeSelector;
